import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ref, onValue, set } from 'firebase/database';
import { database } from '../services/firebase';

interface MaintenanceMessage {
  title: string;
  description: string;
}

interface MaintenanceContextType {
  isMaintenance: boolean;
  maintenanceBO: boolean;
  maintenanceClient: boolean;
  maintenanceConsumer: boolean;
  pinBO: string;
  pinClient: string;
  pinConsumer: string;
  messageBO: MaintenanceMessage;
  messageClient: MaintenanceMessage;
  messageConsumer: MaintenanceMessage;
  setMaintenance: (platform: 'bo' | 'client' | 'consumer', isActive: boolean) => Promise<void>;
  updatePin: (platform: 'bo' | 'client' | 'consumer', pin: string) => Promise<void>;
  updateMessage: (platform: 'bo' | 'client' | 'consumer', message: MaintenanceMessage) => Promise<void>;
  verifyPin: (platform: 'bo' | 'client' | 'consumer', pin: string) => boolean;
}

const DEFAULT_MESSAGE = 'La aplicación está en mantenimiento. Por favor, intente más tarde.';

const MaintenanceContext = createContext<MaintenanceContextType | undefined>(undefined);

export const MaintenanceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isMaintenance, setIsMaintenance] = useState<boolean>(false);
  const [maintenanceBO, setMaintenanceBO] = useState<boolean>(false);
  const [maintenanceClient, setMaintenanceClient] = useState<boolean>(false);
  const [maintenanceConsumer, setMaintenanceConsumer] = useState<boolean>(false);
  const [pinBO, setPinBO] = useState<string>('2222');
  const [pinClient, setPinClient] = useState<string>('1111');
  const [pinConsumer, setPinConsumer] = useState<string>('3333');
  
  const [messageBO, setMessageBO] = useState<MaintenanceMessage>({ title: '', description: '' });
  const [messageClient, setMessageClient] = useState<MaintenanceMessage>({ title: '', description: '' });
  const [messageConsumer, setMessageConsumer] = useState<MaintenanceMessage>({ title: '', description: '' });

  useEffect(() => {
    // Configurar listeners para los estados de mantenimiento
    const maintenanceRef = ref(database, 'maintenance');
    
    const unsubscribe = onValue(maintenanceRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setMaintenanceBO(data.bo?.enabled || false);
        setMaintenanceClient(data.client?.enabled || false);
        setMaintenanceConsumer(data.consumer?.enabled || false);
        setPinBO(data.bo?.pin || '2222');
        setPinClient(data.client?.pin || '1111');
        setPinConsumer(data.consumer?.pin || '3333');
        
        // Actualizar mensajes
        setMessageBO({
          title: data.bo?.message?.title || 'Sitio en mantenimiento',
          description: data.bo?.message?.description || 'Estamos realizando tareas de mantenimiento. Por favor, intente nuevamente más tarde.'
        });
        
        setMessageClient({
          title: data.client?.message?.title || 'Sitio en mantenimiento',
          description: data.client?.message?.description || 'Estamos realizando tareas de mantenimiento. Por favor, intente nuevamente más tarde.'
        });
        
        setMessageConsumer({
          title: data.consumer?.message?.title || 'Sitio en mantenimiento',
          description: data.consumer?.message?.description || 'Estamos realizando tareas de mantenimiento. Por favor, intente nuevamente más tarde.'
        });
      }
    });

    return () => unsubscribe();
  }, []);

  const setMaintenance = async (platform: 'bo' | 'client' | 'consumer', isActive: boolean) => {
    const platformRef = ref(database, `maintenance/${platform}`);
    await set(platformRef, {
      enabled: isActive,
      pin: platform === 'bo' ? pinBO : platform === 'client' ? pinClient : pinConsumer,
      updatedAt: new Date().toISOString()
    });
  };

  const updatePin = async (platform: 'bo' | 'client' | 'consumer', pin: string) => {
    if (pin.length !== 4 || !/^\d+$/.test(pin)) {
      throw new Error('El PIN debe tener exactamente 4 dígitos numéricos');
    }
    const platformRef = ref(database, `maintenance/${platform}`);
    const currentMessage = platform === 'bo' ? messageBO : platform === 'client' ? messageClient : messageConsumer;
    
    await set(platformRef, {
      enabled: platform === 'bo' ? maintenanceBO : platform === 'client' ? maintenanceClient : maintenanceConsumer,
      pin: pin,
      message: currentMessage,
      updatedAt: new Date().toISOString()
    });
  };
  
  const updateMessage = async (platform: 'bo' | 'client' | 'consumer', message: MaintenanceMessage) => {
    if (message.title.length > 100) {
      throw new Error('El título no puede tener más de 100 caracteres');
    }
    if (message.description.length > 300) {
      throw new Error('La descripción no puede tener más de 300 caracteres');
    }
    
    const platformRef = ref(database, `maintenance/${platform}`);
    await set(platformRef, {
      enabled: platform === 'bo' ? maintenanceBO : platform === 'client' ? maintenanceClient : maintenanceConsumer,
      pin: platform === 'bo' ? pinBO : platform === 'client' ? pinClient : pinConsumer,
      message: message,
      updatedAt: new Date().toISOString()
    });
    
    // Actualizar el estado local
    if (platform === 'bo') setMessageBO(message);
    else if (platform === 'client') setMessageClient(message);
    else setMessageConsumer(message);
  };

  const verifyPin = (platform: 'bo' | 'client' | 'consumer', pin: string): boolean => {
    const correctPin = platform === 'bo' ? pinBO : platform === 'client' ? pinClient : pinConsumer;
    return pin === correctPin;
  };

  return (
    <MaintenanceContext.Provider
      value={{
        isMaintenance,
        maintenanceBO,
        maintenanceClient,
        maintenanceConsumer,
        pinBO,
        pinClient,
        pinConsumer,
        messageBO,
        messageClient,
        messageConsumer,
        setMaintenance,
        updatePin,
        updateMessage,
        verifyPin
      }}>
      {children}
    </MaintenanceContext.Provider>
  );
};

export const useMaintenance = (): MaintenanceContextType => {
  const context = useContext(MaintenanceContext);
  if (!context) {
    throw new Error('useMaintenance debe usarse dentro de un MaintenanceProvider');
  }
  return context;
};
